import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { API_CONFIG } from '../../config/api';
import type { ApiError } from '../../utils/apiClient';
import { fileText } from '../kiosk-releases/releaseFiles';
import { errorLine } from '../kiosk-releases/labels';
import { ReleaseKeyError, createReleaseKey, keyFileBlob, openReleaseKey, type SigningKey } from './releaseKeyCrypto';
import { saveBlob } from './saveBlob';

export { saveBlob };

type Translate = (key: string, options?: any) => string;

export function keyErrorLine(error: unknown, translate: Translate): string {
  if (error instanceof ReleaseKeyError) return translate(`resources.kiosk-release-keys.errors.${error.reason}`);
  return errorLine(error as ApiError, translate);
}

export function deploymentWarning(deployment: string, translate: Translate): string | null {
  if (deployment === API_CONFIG.baseURL) return null;
  return translate('resources.kiosk-release-keys.errors.deployment_differs', { deployment, origin: API_CONFIG.baseURL });
}

interface KeyFileOpenerProps {
  fileTestId: string;
  passwordTestId: string;
  fileLabel: string;
  passwordLabel: string;
  disabled?: boolean;
  onOpened: (key: SigningKey, deployment: string) => string | null;
  onFailed?: () => void;
  onReset?: () => void;
}

export const KeyFileOpener: React.FC<KeyFileOpenerProps> = ({ fileTestId, passwordTestId, fileLabel, passwordLabel, disabled = false, onOpened, onFailed, onReset }) => {
  const translate = useTranslate();
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const open = async () => {
    if (file === null) return;
    if (onReset !== undefined) onReset();
    setBusy(true);
    setProblem(null);
    try {
      const text = await fileText(file);
      const key = await openReleaseKey(text, password);
      const refusal = onOpened(key, String(JSON.parse(text).deployment));
      if (refusal !== null) setProblem(translate(`resources.kiosk-release-keys.errors.${refusal}`));
    } catch (error) {
      if (onFailed !== undefined) onFailed();
      setProblem(keyErrorLine(error, translate));
    } finally {
      setPassword('');
      setBusy(false);
    }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">{fileLabel}</Typography>
        <input data-testid={fileTestId} type="file" accept=".json,application/json" disabled={disabled || busy} onChange={(event) => {
          if (onReset !== undefined) onReset();
          setFile(event.target.files && event.target.files.length > 0 ? event.target.files[0] : null);
        }} />
      </Box>
      <TextField
        type="password"
        label={passwordLabel}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        disabled={disabled || busy}
        inputProps={{ 'data-testid': passwordTestId }}
      />
      {problem !== null && <Alert severity="error">{problem}</Alert>}
      <Box>
        <Button variant="outlined" disabled={disabled || busy || file === null || password === ''} onClick={open} sx={{ textTransform: 'none' }}>
          {translate('resources.kiosk-release-keys.actions.check')}
        </Button>
      </Box>
    </Box>
  );
};

interface NewKeyStepsProps {
  disabled?: boolean;
  ready: boolean;
  onReady: (key: SigningKey) => void;
  onDownloaded: () => void;
}

export const NewKeySteps: React.FC<NewKeyStepsProps> = ({ disabled = false, ready, onReady, onDownloaded }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`resources.kiosk-release-keys.${key}`);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [downloadedKeyId, setDownloadedKeyId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const download = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const created = await createReleaseKey(password, confirm, API_CONFIG.baseURL, new Date());
      saveBlob(keyFileBlob(created.file), created.fileName);
      setDownloadedKeyId(created.key.keyId);
      setPassword('');
      setConfirm('');
      onDownloaded();
    } catch (error) {
      setProblem(keyErrorLine(error, translate));
    } finally {
      setBusy(false);
    }
  };
  if (downloadedKeyId === null) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2">{t('create.intro')}</Typography>
        <TextField type="password" label={t('create.password')} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" disabled={busy} inputProps={{ 'data-testid': 'new-password' }} />
        <TextField type="password" label={t('create.confirm')} value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" disabled={busy} inputProps={{ 'data-testid': 'new-confirm' }} />
        {problem !== null && <Alert severity="error">{problem}</Alert>}
        <Box>
          <Button variant="contained" disabled={disabled || busy || password === '' || confirm === ''} onClick={download} sx={{ textTransform: 'none' }}>
            {t('actions.download')}
          </Button>
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Alert severity="info">{t('create.saved')}</Alert>
      {!ready && (
        <>
          <Typography variant="body2">{t('create.checkIntro')}</Typography>
          <KeyFileOpener
            fileTestId="check-file"
            passwordTestId="check-password"
            fileLabel={translate('resources.kiosk-releases.upload.keyFile')}
            passwordLabel={translate('resources.kiosk-releases.upload.password')}
            onOpened={(key) => {
              if (key.keyId !== downloadedKeyId) return 'not_downloaded_key';
              onReady(key);
              return null;
            }}
          />
        </>
      )}
    </Box>
  );
};
